import { useState, useEffect } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { STATUS_CONFIG, CASE_STATUSES } from '../../data/mockData';
import { fetchCases } from '../../api';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  MessageSquare,
  Clock,
  AlertTriangle,
  Eye,
  Image as ImageIcon,
  Edit,
  Save,
  Trash2,
  Upload,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { format } from 'date-fns';

export default function QCDashboard() {
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [comment, setComment] = useState('');
  
  const [showManualVerifyDialog, setShowManualVerifyDialog] = useState(false);
  const [manualVerifyData, setManualVerifyData] = useState({ result: 'verified', notes: '' });
  
  // QC Editable Data States
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState({});
  const [mockImages, setMockImages] = useState([]);
  
  // Cache to retain info on cancel
  const [cachedEdits, setCachedEdits] = useState({});
  const [cachedImages, setCachedImages] = useState({});

  const [cases, setCases] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchCases();
        if (Array.isArray(data)) setCases(data);
      } catch (e) {
        console.error("Failed to fetch cases for QC:", e);
        setCases([]);
      }
    }
    load();
  }, []);

  const qcPendingCases = cases.filter(c => c.status === CASE_STATUSES.QC_PENDING || c.status === 'qc_pending');
  const qcApprovedCases = cases.filter(c => c.status === CASE_STATUSES.QC_APPROVED || c.status === CASE_STATUSES.COMPLETED || c.status === 'completed' || c.status === 'qc_approved');
  const qcRejectedCases = cases.filter(c => c.status === CASE_STATUSES.QC_REJECTED || c.status === CASE_STATUSES.REVISIT || c.status === 'qc_rejected');

  const handleAction = (caseItem, type) => {
    setSelectedCase(caseItem);
    setActionType(type);
    setShowActionDialog(true);
  };

  const submitAction = () => {
    // Handle action submission
    setShowActionDialog(false);
    setComment('');
  };

  const columns = [
    {
      key: 'refNumber',
      header: 'Case ID',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-blue-600">{row.refNumber}</p>
          <p className="text-xs text-slate-500">{row.product.name}</p>
        </div>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Avatar className="w-7 h-7">
            <AvatarImage src={row.client.logo} />
            <AvatarFallback className="text-xs">{row.client.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-sm">{row.client.name}</span>
        </div>
      ),
    },
    {
      key: 'applicantName',
      header: 'Applicant',
      sortable: true,
    },
    {
      key: 'verifiedBy',
      header: 'Verified By',
      render: (row) => row.assignedTo ? (
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={row.assignedTo.avatar} />
            <AvatarFallback className="text-xs">{row.assignedTo.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-sm text-slate-700">{row.assignedTo.name}</span>
        </div>
      ) : '-',
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      render: (row) => (
        <span className="text-sm text-slate-500">
          {format(new Date(row.updatedAt), 'MMM dd, hh:mm a')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {cachedEdits[row.id] && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] uppercase font-semibold">
              Draft Saved
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 text-slate-500 hover:text-slate-900"
            onClick={() => {
              setSelectedCase(row);
              const existingEdits = cachedEdits[row.id];
              setEditableData(existingEdits || {
                applicantName: row.applicantName,
                applicantPhone: row.applicantPhone || '9876543210',
                address: row.address,
                residenceType: 'Owned',
                localityType: 'Urban',
                yearsAtAddress: '5 years'
              });
              
              const existingImages = cachedImages[row.id];
              setMockImages(existingImages || [
                'https://images.unsplash.com/photo-1560521881-ce09059eeffa?w=100',
                'https://images.unsplash.com/photo-1560521882-ce09059eeffa?w=100',
              ]);
              
              setIsEditing(false);
            }}
            data-testid={`view-case-${row.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          {activeTab === 'pending' && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                onClick={() => handleAction(row, 'approve')}
                data-testid={`approve-case-${row.id}`}
              >
                <CheckCircle2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleAction(row, 'reject')}
                data-testid={`reject-case-${row.id}`}
              >
                <XCircle className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={() => handleAction(row, 'revisit')}
                data-testid={`revisit-case-${row.id}`}
                title="Request Revisit"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  setSelectedCase(row);
                  setShowManualVerifyDialog(true);
                }}
                title="Manual Verify"
              >
                <CheckCircle2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  const kpiStats = [
    { label: 'Pending QC', value: qcPendingCases.length, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50 border border-amber-100' },
    { label: 'Approved Today', value: 23, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50 border border-emerald-100' },
    { label: 'Rejected Today', value: 5, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border border-red-100' },
    { label: 'Revisit Requests', value: qcRejectedCases.length, icon: RefreshCw, color: 'text-blue-600', bg: 'bg-blue-50 border border-blue-100' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="qc-dashboard-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">QC Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Review and approve verification submissions</p>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="crm-card p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="pending" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            <Clock className="w-4 h-4" />
            Pending ({qcPendingCases.length})
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Approved ({qcApprovedCases.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 gap-2">
            <XCircle className="w-4 h-4" />
            Rejected/Revisit ({qcRejectedCases.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          <DataTable
            columns={columns}
            data={qcPendingCases}
            searchPlaceholder="Search pending cases..."
            selectable={false}
          />
        </TabsContent>

        <TabsContent value="approved" className="mt-6">
          <DataTable
            columns={columns}
            data={qcApprovedCases}
            searchPlaceholder="Search approved cases..."
            selectable={false}
          />
        </TabsContent>

        <TabsContent value="rejected" className="mt-6">
          <DataTable
            columns={columns}
            data={qcRejectedCases}
            searchPlaceholder="Search rejected cases..."
            selectable={false}
          />
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              {actionType === 'approve' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
              {actionType === 'reject' && <XCircle className="w-5 h-5 text-red-600" />}
              {actionType === 'revisit' && <RefreshCw className="w-5 h-5 text-amber-600" />}
              {actionType === 'approve' ? 'Approve Case' : actionType === 'reject' ? 'Reject Case' : 'Request Revisit'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedCase?.refNumber} - {selectedCase?.applicantName}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-500">Comments {actionType !== 'approve' && <span className="text-red-400">*</span>}</label>
              <Textarea
                placeholder={
                  actionType === 'approve'
                    ? 'Add any notes (optional)...'
                    : actionType === 'reject'
                    ? 'Provide reason for rejection...'
                    : 'Specify what needs to be revisited...'
                }
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="bg-slate-50/50 border-slate-200"
                rows={4}
              />
            </div>
            {actionType === 'revisit' && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800 font-medium">
                    This will send the case back to the field executive for re-verification.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)} className="border-slate-200 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              className={
                actionType === 'approve'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : actionType === 'reject'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }
            >
              {actionType === 'approve' ? 'Approve' : actionType === 'reject' ? 'Reject' : 'Request Revisit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Verification Dialog */}
      <Dialog open={showManualVerifyDialog} onOpenChange={setShowManualVerifyDialog}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Manual Verification</DialogTitle>
            <DialogDescription className="text-slate-500">
              Perform verification manually for {selectedCase?.refNumber}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Verification Status</label>
              <select 
                className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={manualVerifyData.result} 
                onChange={(e) => setManualVerifyData({...manualVerifyData, result: e.target.value})}
              >
                <option value="verified">Verified</option>
                <option value="qc_rejected">Rejected</option>
                <option value="revisit">Needs Review / Revisit</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Remarks</label>
              <Textarea 
                value={manualVerifyData.notes} 
                onChange={(e) => setManualVerifyData({...manualVerifyData, notes: e.target.value})} 
                className="bg-white border-slate-200" 
                placeholder="Enter verification remarks..." 
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualVerifyDialog(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => {
              setShowManualVerifyDialog(false);
              window.alert(`Manual verification submitted as: ${manualVerifyData.result}`);
            }}>
              Submit Updates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Preview Drawer */}
      {selectedCase && !showActionDialog && (
        <Dialog open={!!selectedCase} onOpenChange={(open) => {
          if (!open) {
            setCachedEdits(prev => ({...prev, [selectedCase.id]: editableData}));
            setCachedImages(prev => ({...prev, [selectedCase.id]: mockImages}));
            setSelectedCase(null);
          }
        }}>
          <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-slate-900">{selectedCase.refNumber}</DialogTitle>
                  <DialogDescription className="text-slate-500">
                    {selectedCase.product.name} • {selectedCase.client.name}
                  </DialogDescription>
                </div>
                {!isEditing ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} className="gap-2 bg-slate-50">
                      <Edit className="w-4 h-4" /> {cachedEdits[selectedCase.id] ? 'Resume QC' : 'Edit Data'}
                    </Button>
                    {cachedEdits[selectedCase.id] && (
                      <Button variant="outline" size="sm" onClick={() => {
                        if (window.confirm("Discard all saved draft data and images?")) {
                          const newEdits = {...cachedEdits};
                          delete newEdits[selectedCase.id];
                          setCachedEdits(newEdits);
                          const newImages = {...cachedImages};
                          delete newImages[selectedCase.id];
                          setCachedImages(newImages);
                          
                          // Reset current view
                          setEditableData({
                            applicantName: selectedCase.applicantName,
                            applicantPhone: selectedCase.applicantPhone || '9876543210',
                            address: selectedCase.address,
                            residenceType: 'Owned',
                            localityType: 'Urban',
                            yearsAtAddress: '5 years'
                          });
                          setMockImages([
                            'https://images.unsplash.com/photo-1560521881-ce09059eeffa?w=100',
                            'https://images.unsplash.com/photo-1560521882-ce09059eeffa?w=100',
                          ]);
                        }
                      }} className="text-red-500 border-red-200 bg-red-50 hover:bg-red-100 gap-2">
                        <Trash2 className="w-4 h-4" /> Discard Backup
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button size="sm" onClick={() => {
                    setIsEditing(false);
                    setCachedEdits(prev => ({...prev, [selectedCase.id]: editableData}));
                    setCachedImages(prev => ({...prev, [selectedCase.id]: mockImages}));
                  }} className="gap-2 bg-blue-600 hover:bg-blue-700">
                    <Save className="w-4 h-4" /> Save Draft
                  </Button>
                )}
              </div>
            </DialogHeader>
            <div className="py-4 space-y-6">
              {/* Applicant Info */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Applicant</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Name</p>
                    {isEditing ? (
                        <Input value={editableData.applicantName} onChange={e => setEditableData({...editableData, applicantName: e.target.value})} className="h-8 mt-1 border-slate-200" />
                    ) : (
                        <p className="text-sm text-slate-900">{editableData.applicantName}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    {isEditing ? (
                        <Input value={editableData.applicantPhone} onChange={e => setEditableData({...editableData, applicantPhone: e.target.value})} className="h-8 mt-1 border-slate-200" />
                    ) : (
                        <p className="text-sm text-slate-900">{editableData.applicantPhone}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500">Address</p>
                    {isEditing ? (
                        <Input value={editableData.address} onChange={e => setEditableData({...editableData, address: e.target.value})} className="h-8 mt-1 border-slate-200" />
                    ) : (
                        <p className="text-sm text-slate-900">{editableData.address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Verification Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Verification Details</h4>
                <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-white">
                  <div>
                    <p className="text-xs text-slate-500">Residence Type</p>
                    {isEditing ? (
                        <Input value={editableData.residenceType} onChange={e => setEditableData({...editableData, residenceType: e.target.value})} className="h-8 mt-1 border-slate-200" />
                    ) : (
                        <p className="text-sm text-slate-900">{editableData.residenceType}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Locality</p>
                    {isEditing ? (
                        <Input value={editableData.localityType} onChange={e => setEditableData({...editableData, localityType: e.target.value})} className="h-8 mt-1 border-slate-200" />
                    ) : (
                        <p className="text-sm text-slate-900">{editableData.localityType}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Years at Address</p>
                    {isEditing ? (
                        <Input value={editableData.yearsAtAddress} onChange={e => setEditableData({...editableData, yearsAtAddress: e.target.value})} className="h-8 mt-1 border-slate-200" />
                    ) : (
                        <p className="text-sm text-slate-900">{editableData.yearsAtAddress}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Verification Result</p>
                    <StatusBadge status="success" label="Positive" />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="space-y-3 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">QC Image Manager</h4>
                    <Button size="sm" variant="outline" className="gap-2 bg-slate-50" onClick={() => {
                        setMockImages(prev => [...prev, `https://images.unsplash.com/photo-${Math.floor(Math.random() * 90000000) + 10000000}?w=100`]);
                    }}>
                        <Upload className="w-4 h-4"/> Add Supporting Image
                    </Button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {mockImages.map((src, i) => (
                    <div key={i} className="aspect-square rounded-lg bg-slate-100 overflow-hidden relative group">
                      <img
                        src={src}
                        alt={`Verification ${i}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button 
                            variant="destructive" 
                            size="icon" 
                            className="bg-red-500/80 hover:bg-red-600"
                            onClick={() => setMockImages(prev => prev.filter((_, idx) => idx !== i))}
                          >
                              <Trash2 className="w-4 h-4 text-white" />
                          </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* QC Actions */}
              {activeTab === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={() => handleAction(selectedCase, 'approve')}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleAction(selectedCase, 'reject')}
                    variant="outline"
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </Button>
                  <Button
                    onClick={() => handleAction(selectedCase, 'revisit')}
                    variant="outline"
                    className="flex-1 border-amber-200 text-amber-700 hover:bg-amber-50 gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Revisit
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
